import { type Completion } from '@codemirror/autocomplete';

import { api } from 'common';

import { appendArrayIndex, appendObjectKey } from './referencePath';
import { type AutocompleteLabels } from './types';

const { blockFieldNames, FlowKeywords } = api.FlowDefinition;

const VARIABLE_TYPE = 'variable';

interface AffixOptions {
  prefix: string;
  suffix: string;
  displayPrefix?: string;
  displaySuffix?: string;
}

const DEFAULT_AFFIXES: AffixOptions = {
  prefix: '',
  suffix: '',
};
export const DOUBLE_BRACE_AFFIXES: AffixOptions = {
  prefix: '{{',
  suffix: '}}',
};
const MAXIMUM_DISPLAY_LABEL_LENGTH = 40;
const TRUNCATED_DISPLAY_LABEL_LENGTH = MAXIMUM_DISPLAY_LABEL_LENGTH / 2 - 2;

function trimDisplayLabel(displayLabel: string) {
  if (displayLabel.length > MAXIMUM_DISPLAY_LABEL_LENGTH) {
    return `${displayLabel.slice(0, TRUNCATED_DISPLAY_LABEL_LENGTH)}...${displayLabel.slice(-TRUNCATED_DISPLAY_LABEL_LENGTH)}`;
  }
  return displayLabel;
}

interface GetAutocompleteVariableNameProps {
  availableVariableNames: string[];
  dataFieldNamesByVariableName?: Record<string, string[]>;
  flowParameterNames: string[];
  latestTestRun?: api.FlowRun.FlowRunWithData | null;
  affixes?: AffixOptions;
  sdkCompletions?: Completion[];
  additionalAutoCompleteLabels?: AutocompleteLabels;
}

export function getAutocompleteCompletions(props: GetAutocompleteVariableNameProps): Completion[] {
  const {
    availableVariableNames,
    dataFieldNamesByVariableName,
    flowParameterNames,
    latestTestRun,
    affixes = DEFAULT_AFFIXES,
    sdkCompletions = [],
    additionalAutoCompleteLabels,
  } = props;
  const completions: Completion[] = sdkCompletions;
  const lastDataByBlockName = new Map<string, unknown>();
  if (latestTestRun) {
    for (const block of Object.values(latestTestRun.data.blocks)) {
      if (block.state === api.FlowRun.FlowBlockState.Completed) {
        lastDataByBlockName.set(block.block.name, block.data);
      }
    }
  }

  function addCompletionLabel(label: string, boost: number) {
    completions.push({
      label: `${affixes.prefix}${label}${affixes.suffix}`,
      displayLabel: trimDisplayLabel(
        `${affixes.displayPrefix ?? affixes.prefix}${label}${affixes.displaySuffix ?? affixes.suffix}`
      ),
      type: VARIABLE_TYPE,
      boost,
    });
  }

  if (flowParameterNames.length) {
    addCompletionLabel(FlowKeywords.inputs, 100000);
    flowParameterNames.forEach((name) => {
      addCompletionLabel(`${FlowKeywords.inputs}.${name}`, 100000);
    });
  }

  availableVariableNames.forEach((name) => {
    blockFieldNames.forEach((reference, referenceIndex) => {
      const dataFieldNames = dataFieldNamesByVariableName?.[name];
      const baseLabel = `${name}.${reference}`;

      // If we have data from the last run, use that instead of the static list
      if (reference === 'data' && lastDataByBlockName.has(name)) {
        addDataCompletions(
          baseLabel,
          lastDataByBlockName.get(name),
          completions,
          affixes,
          'processDataFirst'
        );
      }

      // We want the highest boost for the first references, and all boosts to be > 0
      const boost =
        1000 + (blockFieldNames.length - referenceIndex) * (dataFieldNames?.length || 1);
      addCompletionLabel(baseLabel, boost);
      if (reference === 'data' && dataFieldNames) {
        dataFieldNames.forEach((fieldName, fieldIndex) => {
          addCompletionLabel(`${baseLabel}.${fieldName}`, boost - fieldIndex - 1);
        });
      }
    });
  });

  additionalAutoCompleteLabels?.forEach((label) => {
    addCompletionLabel(label, 1);
  });
  return completions;
}

function addDataCompletions(
  path: string,
  data: unknown,
  completions: Completion[],
  affixes: AffixOptions,
  mode: 'addToCompletions' | 'processDataFirst'
): void {
  if (mode === 'addToCompletions') {
    completions.push({
      label: `${affixes.prefix}${path}${affixes.suffix}`,
      displayLabel: trimDisplayLabel(
        `${affixes.displayPrefix ?? affixes.prefix}${path}${affixes.displaySuffix ?? affixes.suffix}`
      ),
      type: VARIABLE_TYPE,
      boost: 1 / path.length, // shorter paths get boosted higher
      info: getObjectInfo(data),
    });
  }

  if (!data || typeof data !== 'object') {
    return;
  }
  if (Array.isArray(data)) {
    for (let i = 0; i < data.length; i++) {
      if (i > 100) {
        /**
         * We believe that a large volume of completions can overwhelming the list.
         * See thread: https://notablehealth.slack.com/archives/C07GDPYQNJY/p1740636643860279
         * Summary:
         * The main discussion points are around performance issues with the UI, specifically sluggishness that appears to be caused
         * by saving a flow, then editing it with the autocomplete suggestions.
         * The users' found a workaround - clone the flow and continue onwards.
         * This suggests that autocomplete content may be the issue.
         *
         * In reality, we should convert this to a context aware autocomplete, and move away from the simplistic `completeFromList` approach.
         */
        break;
      }
      addDataCompletions(
        appendArrayIndex({ path, index: i }),
        data[i],
        completions,
        affixes,
        'addToCompletions'
      );
    }
  } else {
    for (const [key, value] of Object.entries(data)) {
      if (typeof key !== 'string' || !key) {
        continue;
      }
      addDataCompletions(
        appendObjectKey({ path, key }),
        value,
        completions,
        affixes,
        'addToCompletions'
      );
    }
  }
}

function getObjectInfo(obj: unknown): string {
  if (Array.isArray(obj)) {
    return 'array';
  }
  if (obj === null) {
    return 'null';
  }
  return typeof obj;
}
